/**
 * Notification Item
 *
 * Individual notification card with read/unread styling, category icon,
 * relative timestamp, click-through navigation, and dismiss action.
 *
 * @module components/notifications/NotificationItem
 */

import React, { useCallback, forwardRef } from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import NotificationPriorityIcon from './NotificationPriorityIcon';
import type { Notification } from '../../entities/notification/types';

interface NotificationItemProps {
  notification: Notification;
  /** Called when the notification is clicked (mark read + navigate) */
  onPress: (notification: Notification) => void;
  /** Called when the dismiss button is clicked */
  onDismiss: (id: string) => void;
}

/**
 * Single notification row with visual read/unread distinction
 * forwardRef: Framer Motion `PopChild` may attach a ref for layout measurements.
 */
const NotificationItem = forwardRef<HTMLDivElement, NotificationItemProps>(function NotificationItem(
  { notification, onPress, onDismiss },
  ref,
) {
  const { t } = useTranslation('notifications');
  const { id, title, message, category, read, createdAt, priority } =
    notification;

  const handlePress = useCallback(() => {
    onPress(notification);
  }, [notification, onPress]);

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDismiss(id);
    },
    [id, onDismiss]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onPress(notification);
      }
    },
    [notification, onPress]
  );

  const parsedDate = new Date(createdAt);
  const relativeTime = isNaN(parsedDate.getTime())
    ? t('item.justNow')
    : formatDistanceToNow(parsedDate, { addSuffix: true });

  const isHighPriority = priority === 'HIGH' || priority === 'CRITICAL';

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, transition: { duration: 0.15 } }}
      transition={{ duration: 0.2 }}
      role="button"
      tabIndex={0}
      onClick={handlePress}
      onKeyDown={handleKeyDown}
      className={`group relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 ${
        read
          ? 'bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800/50'
          : 'bg-emerald-50/50 hover:bg-emerald-50 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/30'
      }`}
      aria-label={read ? `${title}. ${relativeTime}` : t('item.unread', { title, time: relativeTime })}
    >
      {/* Unread indicator dot */}
      {!read && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2">
          <div
            className={`h-1.5 w-1.5 rounded-full ${
              isHighPriority
                ? 'bg-red-500'
                : 'bg-emerald-500'
            }`}
          />
        </div>
      )}

      {/* Category icon */}
      <NotificationPriorityIcon category={category} />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm leading-snug ${
            read
              ? 'font-normal text-gray-700 dark:text-gray-300'
              : 'font-medium text-gray-900 dark:text-gray-100'
          }`}
        >
          {title}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400 line-clamp-2">
          {message}
        </p>
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
          {relativeTime}
        </p>
      </div>

      {/* Dismiss button -- visible on hover */}
      <button
        onClick={handleDismiss}
        className="shrink-0 rounded-md p-1 text-gray-400 opacity-0 transition-opacity duration-150 hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100 focus:opacity-100 dark:hover:bg-gray-800 dark:hover:text-gray-300"
        aria-label={t('item.dismiss', { title })}
        tabIndex={-1}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
});

export default NotificationItem;
